/**
 * Page for the creation of module's task.
 *
 * Copyright Ircam amplify 2024-2025
 */

import { useEffect, useRef, useState } from 'react'
import { BiArrowBack, BiBot, BiLoaderAlt } from 'react-icons/bi'
import { useParams } from 'react-router'
import { useNavigate } from 'react-router-dom'
import {
  ModuleConfig,
  MultiInputMode,
} from '../components/ModuleConfig/ModuleConfig'
import ModuleConfigList from '../components/ModuleConfig/ModuleConfigList'
import {
  InputCondition,
  ModuleMimeType,
} from '../components/ModuleConfig/ModuleDef'
import { ModuleInputConfig } from '../components/ModuleConfig/ModuleInputConfig'
import { SORT_DESCENDING } from '../components/TaskList/JobListDef'
import FileUploader from '../components/jobui/FileUploader'
import ModuleParamContainer from '../components/jobui/ModuleParamContainer'
import MultiFileUploader from '../components/jobui/MultiFileUploader'
import {
  AudioFileCheck,
  AudioSpec,
  UploadFileInfo,
  isFileUploaded,
  isRequirementMet,
} from '../components/jobui/UploadFileInfo'
import {
  calculateCost,
  requestJobProcessing,
  standardErrorMsg,
} from '../components/jobui/utils/functions'
import {
  getUserState,
  setJobsSortDirection,
  useDispatch,
  useSelector,
} from '../store'
import { FileUtil } from '../utils/FileUtil'
import { IampLogger, LogTag } from '../utils/IampLogger'
import { TextUtil } from '../utils/TextUtil'

const KEY_JOB_LABEL = 'job_label'
const MAX_FILE_COUNT_PER_INPUT = 20

export enum CostComputeStatus {
  NotReady, // 0
  ReadyToCompute, // 1
  Computing, // 2
  Computed, // 3
  Error, // 4
}

export enum FileListStatus {
  NotReady, // 0
  PartiallyReady, //1
  Ready, // 2
}

/**
 * Return the input configuration of an input file.
 *
 * @param fileId
 *    The ID of the input file, in the form of `<inputId>`
 *    or `<inputId>_<index>`.
 * @param moduleConfig
 *    The configuration of the module.
 * @returns
 *    A tuple containing the input ID of the input and the configuration.
 *    If the configuration is not found, the tuple contains the input ID and
 *    `undefined`.
 */
export function getInputConfig(
  fileId: string,
  moduleConfig: ModuleConfig
): [string, ModuleInputConfig | undefined] {
  // Get the input ID with the index suffix
  const inputId = fileId.split('_')[0]

  // Search for the input in the configuration
  const inputConfig = moduleConfig.inputList.find(
    (input) => input.name === inputId
  )
  return [inputId, inputConfig]
}

/**
 * The React component for the creation of a module's task .
 */
export const TaskCreationPage = () => {
  const { moduleName } = useParams()
  const navigate = useNavigate()

  const dispatch = useDispatch()
  const { companyConsumption } = useSelector(getUserState)

  /**
   * The ModuleConfig instance coming from availableModulesList.
   */
  const [moduleConfig, setModuleConfig] = useState<ModuleConfig | undefined>(
    undefined
  )

  const [multiInputMode, setMultiInputMode] = useState<
    MultiInputMode | undefined
  >(undefined)

  /**
   * The structure storing the input of the module, like files,
   * custom parameters.
   */
  const [inputData, setInputData] = useState<Record<string, UploadFileInfo>>({})

  /**
   * The list of optional parameters for the processing.
   *
   * Key: the parameter ID.
   * Value: A value, string or number.
   */
  const [paramList, setParamList] = useState<Record<string, any>>({})

  /**
   * The estimated cost of the job.
   */
  const [jobCost, setJobCost] = useState<number | undefined>(undefined)

  const [isSaving, setIsSaving] = useState(false)

  const [costComputeState, setCostComputeState] = useState(
    CostComputeStatus.NotReady
  )
  const [isReadyToRun, setReadyToRun] = useState(false)

  /**
   * The audio length in seconds of the smallest uploaded audio files.
   */
  const [audioLength, setAudioLength] = useState<number | undefined>(undefined)

  const [fileListStatus, setFileListStatus] = useState(FileListStatus.NotReady)
  const [isParamListReady, setParamListReady] = useState(false)
  const [moduleErrorMsg, setModuleErrorMsg] = useState<any>(undefined)
  const [hasEnoughCredit, setEnoughCredit] = useState<boolean | undefined>(
    undefined
  )
  const [taskName, setTaskName] = useState<string | undefined>(undefined)

  // Audio context management for Audio file processing
  const audioContextRef = useRef<AudioContext | null>(null)
  if (!audioContextRef.current) {
    // Create once when the component mounts
    audioContextRef.current = new AudioContext()
  }

  /**
   * Get the configuration of the selected module, given its name.
   */
  useEffect(() => {
    if (moduleName !== undefined) {
      const module = Object.values(ModuleConfigList).find(
        (module) => module.info.urlPathSegment === moduleName
      )

      if (module) {
        if (
          companyConsumption?.subscription_tier === module?.authorized_tiers ||
          !module?.authorized_tiers
        ) {
          setModuleConfig(module)
          document.title = `${module.info.title} | ${window.location.host}`
        } else {
          navigate('/job-creation', { replace: true })
        }
      } else {
        navigate('/job-creation', { replace: true })
      }
    }
  }, [moduleName])

  /**
   * Update states when the configuration of the module is retrieved.
   */
  useEffect(() => {
    if (moduleConfig) {
      if (moduleConfig.multiplicity) {
        // Get multiplicity available in the configuration.
        setMultiInputMode(moduleConfig.multiplicity)
      } else {
        // Get multiplicity depending on the input configuration.
        if (moduleConfig.inputList.length > 1) {
          // More than one type of input are needed: this module can only create
          // a unique job with the inputs.
          setMultiInputMode(MultiInputMode.OneSingleJob)
        } else if (moduleConfig.inputList.length == 1) {
          // One type
          if (moduleConfig.inputList[0].multiple) {
            setMultiInputMode(MultiInputMode.OneJobMultipleInput)
          } else {
            setMultiInputMode(MultiInputMode.OneJobPerInputFile)
          }
        }
      }
    }
  }, [moduleConfig])

  /**
   * Enable or disable the Save button, depending on the error detection and
   * set parameters.
   */
  useEffect(() => {
    IampLogger.debug(
      `fileListStatus: ${fileListStatus}, ` +
        `isParamListReady: ${isParamListReady}, ` +
        `hasEnoughCredit: ${hasEnoughCredit}, `
    )

    setReadyToRun(
      FileListStatus.NotReady !== fileListStatus &&
        isParamListReady &&
        true === hasEnoughCredit
    )
  }, [fileListStatus, isParamListReady, hasEnoughCredit])

  /**
   * Increment an ID with the format <name>_<index>, e.g. "audioFile_1".
   *
   * @param currentId The ID to increment.
   * @returns The new ID or undefined if the ID has not the expected format.
   */
  function incrementId(currentId: string): string | undefined {
    // Split the ID into base and index parts
    const parts = currentId.split('_')

    // Ensure there are two parts
    if (parts.length == 2) {
      const baseId = parts[0]
      const index = parseInt(parts[1])

      if (isNaN(index)) {
        throw new Error('Index part of ID is not a number.')
      }

      // Increment the index
      const newIndex = index + 1

      // Construct the new ID
      return `${baseId}_${newIndex}`
    } else {
      return undefined
    }
  }

  /**
   * Get the name of an input given a string in the format <name>_<index>
   * or <name>.
   *
   * @param name The string to parsed.
   * @returns The name without index.
   */
  const getInputName = (name: string): string => {
    const lastUnderscoreIndex = name.lastIndexOf('_')
    if (lastUnderscoreIndex === -1) {
      // No underscore, return as is
      return name
    }

    const afterUnderscore = name.substring(lastUnderscoreIndex + 1)
    if (/^\d+$/.test(afterUnderscore)) {
      // Check if only numbers
      return name.substring(0, lastUnderscoreIndex)
    }

    return name // Return unchanged if not only numbers
  }

  /**
   * Check if the given file fulfills the requirements of the module.
   *
   * @param fileInfo The information about the file to upload.
   * @param inputName The name of the input of the module.
   * @returns AudioFileCheck or undefined.
   */
  const checkFileRequirements = async (
    fileInfo: UploadFileInfo,
    inputName: string
  ): Promise<AudioFileCheck | undefined> => {
    if (!moduleConfig || !fileInfo.localFile || !audioContextRef.current) {
      return undefined
    }

    // Search for the input configuration
    let inputConfig = undefined
    const inputConfigName = getInputName(inputName)
    for (const currentConfig of moduleConfig.inputList) {
      if (currentConfig.name === inputConfigName) {
        inputConfig = currentConfig
      }
    }

    if (!inputConfig) {
      // Not found
      return undefined
    }

    // Check audio file
    const checking = new AudioFileCheck(inputConfig)

    if (inputConfig.mimeTypeList) {
      // Convert list of ModuleMimeType to a list of strings
      const mimeStringList: string[] = [...inputConfig.mimeTypeList]

      checking.isTypeValid = mimeStringList.includes(
        fileInfo.mimetype as ModuleMimeType
      )
    }

    // Check audio specifications in case audio files
    if (fileInfo.mimetype.startsWith('audio/')) {
      const audioSpec = new AudioSpec()

      if (fileInfo.remoteFile?.fileinfo) {
        // Got the audio specifications extracted by the backend
        // in a request response
        audioSpec.parseBackEndResponse(fileInfo.remoteFile?.fileinfo)
      } else {
        // Try to extract the audio specifications
        // with the browser's native decoder
        try {
          await audioSpec.decodeAudioFile(
            audioContextRef.current,
            fileInfo.localFile
          )
        } catch (decodeError) {
          // Can't decode the file: postpone the checking
          // until the file is uploaded and use the metadata retrieved
          // by the Storage module.
          IampLogger.error(
            'Failed to decode audio data with Web AudioContext:',
            decodeError
          )
          checking.isTypeValid = undefined
        }
      }

      if (audioSpec) {
        checking.isTypeValid = true

        // Check minimum duration
        if (audioSpec.durationInSec && inputConfig.minDuration) {
          checking.isMinDurationValid =
            audioSpec.durationInSec >= inputConfig.minDuration
        }
        setAudioLength(audioSpec.durationInSec)

        // Check maximum duration
        if (audioSpec.durationInSec && inputConfig.maxDuration) {
          checking.isMaxDurationValid =
            audioSpec.durationInSec <= inputConfig.maxDuration
        }

        // Check number of channels
        if (audioSpec.channelCount && inputConfig.channelCountList) {
          checking.isChannelCountValid = inputConfig.channelCountList.includes(
            audioSpec.channelCount
          )
        }
      }
    }

    return checking
  }

  /**
   * Check if the files of the given list fulfill the requirements of the
   * module.
   *
   * @param inputId The ID of the input.
   * @param fileInfoList The list of files.
   * @param currentInputData The current status of inputData.
   * @returns The updated inputData to set, with requirements status.
   */
  const checkFileList = async (
    inputId: string,
    fileInfoList: UploadFileInfo[],
    currentInputData: Record<string, UploadFileInfo>
  ) => {
    // Add input files
    let currentId: string | undefined = inputId

    const promises = fileInfoList.map(async (file) => {
      if (currentId) {
        if ( //TODO: on Safari, MP3 files must checked again after upload
          !file.requirementCheck ||
          file.requirementCheck.isValid() === undefined
        ) {
          try {
            IampLogger.debug(`Checking ${file.fileName}`)
            const checking = await checkFileRequirements(file, inputId)
            if (checking) {
              IampLogger.debug('Checking:', checking)
              file.requirementCheck = checking
            }
          } catch (error) {
            IampLogger.error('Error while checking file requirements:', error)
          }
        } else {
          IampLogger.debug(`${file.fileName} already checked.`)
        }

        const currentData = currentInputData[currentId]
        const isUploaded =
          file.isUploaded === true ||
          (FileUtil.areFilesEqual(file.localFile, currentData?.localFile) &&
            currentData?.isUploaded === true)
        const remoteFile = file.remoteFile ?? currentData?.remoteFile

        const fileCount = Object.keys(currentInputData).length
        if (
          (currentData && fileCount <= MAX_FILE_COUNT_PER_INPUT) ||
          (!currentData && fileCount < MAX_FILE_COUNT_PER_INPUT)
        ) {
          // Update fields concerning the file, and preserving isUploaded status
          currentInputData[currentId] = {
            ...currentData, // Existing data
            ...file, // New or updated data
            isUploaded,
            remoteFile,
          }
        }

        // Next ID
        currentId = incrementId(currentId)
      }
    })

    await Promise.all(promises)
    return currentInputData
  }

  /**
   * Add files to the list of inputs.
   *
   * @param inputId The ID of the input.
   * @param fileInfoList The list of files.
   *
   * If more than one file is in fileInfoList, they are set to the inputs
   * following designated by inputId.
   */
  const addInputFile = async (
    inputId: string,
    fileInfoList: UploadFileInfo[] | undefined
  ) => {
    if (fileInfoList) {
      const fileNames = fileInfoList.map((f) => `"${f.fileName}"`).join(', ')
      IampLogger.debug(LogTag.TaskCreationPage, `Files to add: [${fileNames}]`)

      setCostComputeState(CostComputeStatus.NotReady)
      setJobCost(undefined)
      setEnoughCredit(undefined)
      setModuleErrorMsg(undefined)

      // Use functional state update to let React handle concurrent calls
      // and  process files.
      setInputData((prevState) => {
        const currentInputData = { ...prevState }
        // Process files asynchronously
        checkFileList(inputId, fileInfoList, currentInputData).then(
          (checkedInputData) => {
            // Merge the processed data into the latest state
            // after async operation completes
            setInputData((latestState) => ({
              ...latestState,
              ...checkedInputData,
            }))
          }
        )
        // Return the current state as is for now
        return currentInputData
      })
    } else {
      // Handle the case where no fileInfoList is provided
      setInputData((prevState: any) => {
        const updatedState = { ...prevState }
        delete updatedState[inputId]
        IampLogger.debug(`Removed ${inputId} file.`)
        return updatedState
      })
    }
  }

  /**
   * Set the parameters of the module.
   *
   * @param paramId The ID of the parameter.
   * @param value The value of the parameter. If 'None' or undefined,
   *  delete the existing value of the parameter.
   */
  const setConfigParam = (paramId: string, value: any) => {
    setParamList((prevState: any) => {
      let updatedParamList = { ...prevState }

      if (value !== undefined && value !== 'None') {
        // Ensure 'paramList' is defined
        if (!updatedParamList) {
          updatedParamList = {}
        }

        // Add the parameter and its value, converted to a number if applicable.
        updatedParamList[paramId] = TextUtil.normalizeValue(value)
      } else if (prevState?.[paramId]) {
        // Delete the parameter with no value.
        delete prevState[paramId]
      }

      return updatedParamList
    })
  }

  /**
   * Check if all the requirements (files and parameters) are fulfilled.
   *
   * Files must be uploaded to the remote, the minimum number of configuration
   * parameters must be set.
   */
  useEffect(() => {
    // Check that the files which meet all requirements are uploaded.
    // Invalid files are not uploaded and don't need to be checked.
    let uploadListStatus = FileListStatus.Ready
    for (const [, file] of Object.entries(inputData)) {
      if (isRequirementMet(file)) {
        if (!isFileUploaded(file)) {
          // Valid file not yet completely uploaded
          uploadListStatus = FileListStatus.NotReady
          break
        }
      } else {
        uploadListStatus = FileListStatus.PartiallyReady
      }
    }

    // Check non-optional files
    let isMandatoryListOk = undefined
    for (const inputConfig of moduleConfig?.inputList || []) {
      if (InputCondition.Optional !== inputConfig?.condition) {
        // Check if there is enough file
        // for this non-optional input configuration
        let inputCount = 0

        // Search uploaded files for that input configuration
        for (const [fileId, fileInfo] of Object.entries(inputData)) {
          if (getInputName(fileId) == inputConfig.name) {
            if (fileInfo.requirementCheck?.isValid()) {
              ++inputCount
            }
          }
        }

        if (inputCount >= (inputConfig.minMultipleInputCount ?? 1)) {
          // Enough non-optional files for this input configuration
          isMandatoryListOk = true
          break
        }
      }
    }

    // Check minimum number of configuration parameters.
    let isParamListOk = undefined
    if (moduleConfig?.minParamCount && moduleConfig.minParamCount > 0) {
      IampLogger.debug(`minParamSet: ${paramList}`)
      const paramSetCount = Object.keys(paramList).length
      isParamListOk = paramSetCount >= moduleConfig.minParamCount
    } else {
      // Check mandatory parameters
      const mandatoryParamList = (moduleConfig?.paramList ?? []).filter(
        (elt) => elt?.mandatory === true
      )
      let isMandatoryParamSet = true
      if (mandatoryParamList.length > 0) {
        for (const elt of mandatoryParamList) {
          // Check which mandatory parameter has not been set
          if (
            paramList[elt.name] === undefined ||
            paramList[elt.name] === 'None' ||
            paramList[elt.name].length === 0
          ) {
            isMandatoryParamSet = false
            break
          }
        }
      }
      isParamListOk = isMandatoryParamSet
    }

    const listReady = isMandatoryListOk
      ? uploadListStatus
      : FileListStatus.NotReady
    setFileListStatus(listReady)

    if (isMandatoryListOk) {
      if (FileListStatus.PartiallyReady === uploadListStatus) {
        // Display warning
        setModuleErrorMsg(
          'Some files are unsupported and will not be processed'
        )
      } else {
        // Clear any warning message.
        setModuleErrorMsg('')
      }
    }

    setParamListReady(true === isParamListOk)
    if (FileListStatus.NotReady !== listReady && isParamListOk) {
      setCostComputeState(CostComputeStatus.ReadyToCompute)
    } else {
      setCostComputeState(CostComputeStatus.NotReady)
    }
  }, [inputData, paramList])

  /**
   * Compute the cost of the processing when all files are uploaded.
   */
  useEffect(() => {
    if (CostComputeStatus.NotReady === costComputeState) {
      setJobCost(undefined)
      setEnoughCredit(undefined)
    } else if (CostComputeStatus.ReadyToCompute === costComputeState) {
      // Get ias URLs of the audio files
      const iasAudioList: string[] = []
      for (const inputFile of Object.values(inputData).filter((inputFile) => {
        return (
          FileUtil.isAudioMimeType(inputFile.mimetype) &&
          isFileUploaded(inputFile) &&
          isRequirementMet(inputFile)
        )
      })) {
        IampLogger.debug(`Compute cost with ${inputFile.fileName}`)
        iasAudioList.push(inputFile?.remoteFile?.ias)
      }

      setJobCost(undefined)
      setEnoughCredit(undefined)

      if (iasAudioList.length > 0 && moduleConfig?.info.serviceName) {
        setCostComputeState(CostComputeStatus.Computing)
        calculateCost(iasAudioList, moduleConfig.info.serviceName)
          .then((response) => {
            const data = response.data
            if (data) {
              IampLogger.debug('calculateCost response:', data)
              setJobCost(data.job_cost ?? undefined)
              setEnoughCredit(data.can_execute ?? undefined)
              setCostComputeState(CostComputeStatus.Computed)
            }
          })
          .catch((error) => {
            IampLogger.error('Error during calculateCost:', error)
            setJobCost(undefined)
            setEnoughCredit(undefined)
            setCostComputeState(CostComputeStatus.Error)
            setModuleErrorMsg(
              error.response?.data?.message ??
                standardErrorMsg(error.response.status)
            )
          })
      }
    }
  }, [Boolean(moduleConfig), costComputeState])

  /**
   * The component which displays the cost of the job.
   */
  const jobCostComponent = () => {
    let comp = undefined

    if (CostComputeStatus.Computing === costComputeState) {
      comp = <BiLoaderAlt className="loadingIcon" />
    } else if (jobCost === undefined) {
      comp = ''
    } else {
      const unit = `credit${jobCost != 1.0 ? 's' : ''}`
      comp = `${Number((jobCost ?? 0).toFixed(2))} ${unit}`
    }

    return <>Estimated cost: {comp}</>
  }

  /**
   * Return the input configuration of an input file.
   *
   * @param fileId
   *    The ID of the input file, in the form of `<inputId>`
   *    or `<inputId>_<index>`.
   * @param moduleConfig
   *    The configuration of the module.
   * @returns
   *    A tuple containing the input ID of the input and the configuration.
   *    If the configuration is not found, the tuple contains the input ID and
   *    `undefined`.
   */
  function getInputConfig(
    fileId: string,
    moduleConfig: ModuleConfig
  ): [string, ModuleInputConfig | undefined] {
    // Get the input ID with the index suffix
    const inputId = fileId.split('_')[0]

    // Search for the input in the configuration
    const inputConfig = moduleConfig.inputList.find(
      (input) => input.name === inputId
    )
    return [inputId, inputConfig]
  }

  /**
   * Generate the payloads to send to the back-end.
   *
   * @param inputData The data of the inputs.
   * @param taskName The name of the task, or undefined.
   * @returns A list of payloads.
   */
  const getPayloadList = (
    inputData: any,
    taskName: string | undefined
  ): Record<string, any>[] | undefined => {
    if (!moduleConfig) {
      // Do nothing
      return undefined
    }
    const payloadList: Record<string, any>[] = []
    const job_label = taskName

    // Configuration parameters
    const paramSet: Record<string, any> = {}
    if (paramList) {
      for (const [paramName, paramValue] of Object.entries(paramList)) {
        paramSet[paramName] = paramValue
      }
    }

    const getCompleteJobLabel = (
      job_label: string | undefined,
      file_name: string | undefined = undefined,
      index: number | undefined = undefined,
      job_count: number
    ): string | undefined => {
      let res: string | undefined = undefined

      if (job_label && job_label.length > 0) {
        res = job_label
        if (res && index != undefined && job_count > 1) {
          res = `${res} (${index + 1})`
        }
      } else {
        res = file_name
      }

      return res
    }

    // Get the list of valid input files
    const validFileList = Object.entries(inputData).reduce(
      (acc, [key, inputFile]) => {
        const info = inputFile as UploadFileInfo
        if (isFileUploaded(info) && isRequirementMet(info)) {
          acc[key] = info
        }
        return acc
      },
      {} as Record<string, UploadFileInfo>
    )
    const validCount = Object.keys(validFileList).length

    let payload: Record<string, any> = { ...paramSet }

    for (const [index, [key, fileInfo]] of Object.entries(
      validFileList
    ).entries()) {
      const [inputId, inputConfig] = getInputConfig(key, moduleConfig)
      if (inputConfig) {
        // Found the input
        if (inputConfig['multiple']) {
          // Accept several input files in one job
          if (!payload[inputId]) {
            payload[inputId] = []
          }
          payload[inputId].push(fileInfo.remoteFile?.ias)
        } else {
          // One input file per job
          if (multiInputMode == MultiInputMode.OneJobPerInputFile) {
            if (payload[inputId]) {
              // The current payload already has an input file:
              // add it to the list of payloads.
              if (!payload[KEY_JOB_LABEL]) {
                // Set the job label if needed
                payload[KEY_JOB_LABEL] = getCompleteJobLabel(
                  job_label,
                  fileInfo.fileName,
                  index - 1,
                  validCount
                )
              }
              payloadList.push(payload)
              // Prepare new payload for the next file
              payload = { ...paramSet }
            }
          }

          payload[inputId] = fileInfo.remoteFile?.ias

          // Set job label if needed
          if (multiInputMode == MultiInputMode.OneJobPerInputFile) {
            payload[KEY_JOB_LABEL] = getCompleteJobLabel(
              job_label,
              fileInfo.fileName,
              index,
              validCount
            )
          }
        }
      }
    }

    if (!payload[KEY_JOB_LABEL] && job_label) {
      payload[KEY_JOB_LABEL] = job_label
    }

    payloadList.push(payload)

    return payloadList
  }

  /**
   * Create the task job.
   */
  const handleCreateJobWithCognito = async () => {
    if (!moduleConfig) {
      // Do nothing
      return
    }

    if (!isSaving && moduleConfig?.info.endpoint !== undefined) {
      setIsSaving(true)
      try {
        IampLogger.debug('Create job:', inputData)
        const payloadList = getPayloadList(inputData, taskName)
        if (payloadList && payloadList.length > 0) {
          for (const [index, payload] of (payloadList ?? []).entries()) {
            IampLogger.debug(`payload ${index}: `, payload)

            // Go to Task history page.
            const response = await requestJobProcessing(
              JSON.stringify(payload),
              moduleConfig?.info.endpoint
            )
            IampLogger.debug('request response:', response)
          }
          dispatch(setJobsSortDirection(SORT_DESCENDING))
          navigate('/jobs')
        }
      } catch (e) {
        IampLogger.error('handleCreateJobWithCognito:', e)
      }
      setIsSaving(false)
    }
  }

  const handleTaskNameChange = (e: any) => {
    const { value } = e.target
    const trimmed = value?.trim()
    setTaskName(trimmed)
  }

  return (
    <div className="jobui-globalContainer">
      <div className="jobui-createContainer">
        <div className="header !pr-[50px]">
          <h1
            id="task-creation-page-title"
            className="text-primary-900 font-medium text-[30px] pl-[10px]"
          >
            {moduleConfig?.info.title ?? ''}
          </h1>
          <button
            type="button"
            className="pBtn"
            onClick={() => navigate('/job-creation')}
          >
            <BiArrowBack />
            <h4>Back</h4>
          </button>
        </div>
        {moduleConfig?.api_doc_url !== undefined && (
          <button
            type="button"
            className="docBtn"
            onClick={() => window.open(moduleConfig.api_doc_url, '_blank')}
          >
            Read the documentation
          </button>
        )}

        {moduleConfig !== undefined && (
          <div className="configContainer">
            <div className="filesContainer">
              <h3>What are we working with?</h3>
              <div>
                {
                  /** Display an upload widget for each input of the module. */
                  moduleConfig?.inputList &&
                    moduleConfig.inputList.map(
                      (currentInput: ModuleInputConfig) =>
                        currentInput?.multiple ||
                        MultiInputMode.OneJobPerInputFile === multiInputMode ? (
                          <div
                            key={currentInput.name}
                            className="jobui-section2 dashContainer"
                          >
                            {/** Multiple input */}
                            <MultiFileUploader
                              tip={currentInput.tip}
                              title={currentInput.title}
                              inputData={inputData}
                              addInputFile={addInputFile}
                              inputId={currentInput.name}
                              mimeTypes={
                                moduleConfig?.inputList?.[0]?.mimeTypeList
                              }
                              maxInputCount={MAX_FILE_COUNT_PER_INPUT}
                              inputCondition={currentInput.condition}
                            />
                          </div>
                        ) : (
                          <div
                            key={currentInput.name}
                            className="jobui-section2 dashContainer FileUploader"
                          >
                            {/** Single input */}
                            <FileUploader
                              id={TextUtil.replaceSpaces(
                                `${moduleConfig.info.title}_${currentInput.name}`
                              )}
                              tip={currentInput.tip}
                              title={currentInput.title}
                              mimeTypes={currentInput.mimeTypeList}
                              inputData={inputData}
                              inputFileToUpload={inputData?.[currentInput.name]}
                              addInputFile={addInputFile}
                              inputId={currentInput.name}
                              isMultipleSelection={false}
                              inputCondition={currentInput.condition}
                            />
                          </div>
                        )
                    )
                }
              </div>

              <div className="jobui-section">
                <div>
                  <div className="costEstimation">
                    <h4>{jobCostComponent()}</h4>
                  </div>
                  <div className="taskName">
                    <h4>Task name</h4>
                    <input
                      type="text"
                      name={KEY_JOB_LABEL}
                      aria-label="Task name (optional)"
                      maxLength={40}
                      onChange={handleTaskNameChange}
                      placeholder="Type in this task name..."
                    ></input>
                  </div>
                </div>
              </div>

              <div className="accentText" id="module-error-message">
                {moduleErrorMsg}
              </div>
              {hasEnoughCredit === false && (
                <p>
                  Insufficient credits to complete the task.
                  <br />
                  Please upgrade your plan in the Billing section to further use
                  the platform.
                  <br />
                </p>
              )}

              <div className="btnGrp createFooter">
                <button
                  type="button"
                  className={'bigBtn !text-white'}
                  onClick={handleCreateJobWithCognito}
                  disabled={!isReadyToRun}
                >
                  {isSaving ? (
                    <BiLoaderAlt className="loadingIcon !text-white" />
                  ) : (
                    <BiBot className="text-white" />
                  )}
                  Save & Run
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/job-creation')}
                  className="bigBtn2"
                >
                  Cancel
                </button>
              </div>
            </div>

            <div className="paramsContainer">
              {moduleConfig?.paramList !== undefined ? (
                <ModuleParamContainer
                  title="Shape the sound, the way you want"
                  paramsTip={moduleConfig.paramsTip}
                  moduleParams={moduleConfig.paramList}
                  configParamList={paramList}
                  setConfigParam={setConfigParam}
                  sourceDuration={audioLength ?? 0}
                />
              ) : (
                <div>
                  <h3>Shape the sound, the way you want</h3>
                  <p>No settings required to use this module.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
